import React, { useRef, useState } from 'react';
import { X, Upload, Download, AlertTriangle, CheckCircle, FileSpreadsheet } from 'lucide-react';
import { useInventoryStore } from '../../store/inventoryStore';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useOwnersStore } from '../../store/ownersStore';
import { useProjectStore } from '../../store/projectStore';
import { downloadInventoryTemplate, downloadInventoryUnitsTemplate } from '../../utils/inventoryExcel';
import {
  ImportKind,
  TypesImportMode,
  DuplicateAction,
  ParsedTypeRow,
  UnitImportPreview,
  TypesImportResult,
  UnitsImportResult,
  parseTypesWorkbook,
  parseUnitsWorkbook,
  buildUnitPreview,
  runTypesImport,
  runUnitsImport,
} from '../../utils/inventoryExcelImport';

interface Props {
  onClose: () => void;
  onDone: () => void;
}

type Step = 'options' | 'preview' | 'result';

const ExcelImportModal: React.FC<Props> = ({ onClose, onDone }) => {
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('options');
  const [importKind, setImportKind] = useState<ImportKind>('types');
  const [importMode, setImportMode] = useState<TypesImportMode>('add');
  const [duplicateAction, setDuplicateAction] = useState<DuplicateAction>('skip');
  const [fileName, setFileName] = useState('');
  const [typeRows, setTypeRows] = useState<ParsedTypeRow[]>([]);
  const [unitPreview, setUnitPreview] = useState<UnitImportPreview | null>(null);
  const [parseError, setParseError] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [typesResult, setTypesResult] = useState<TypesImportResult | null>(null);
  const [unitsResult, setUnitsResult] = useState<UnitsImportResult | null>(null);

  const modalBg  = isDark ? '#20242b' : '#ffffff';
  const pageBg   = isDark ? '#171a1f' : '#f3f4f6';
  const border   = isDark ? '#343a43' : '#e5e7eb';
  const text     = isDark ? '#e5e7eb' : '#111827';
  const muted    = isDark ? '#9ca3af' : '#6b7280';
  const inputBg  = isDark ? '#171a1f' : '#f9fafb';
  const headerBg = isDark ? '#20242b' : '#f0f1f3';

  const labelCls = 'text-sm font-medium mb-1.5 block';

  const fallbackWarehouseName = () => {
    const wh = useWarehouseStore.getState();
    const id = wh.activeWarehouseId ?? wh.defaultWarehouseId;
    return wh.warehouses.find(w => w.id === id)?.name ?? wh.warehouses[0]?.name ?? null;
  };

  const resetFile = () => {
    setTypeRows([]);
    setUnitPreview(null);
    setFileName('');
    setParseError('');
    setTypesResult(null);
    setUnitsResult(null);
  };

  const handleFile = async (file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const buf = e.target?.result;
      if (!(buf instanceof ArrayBuffer)) {
        setParseError('Failed to read the file.');
        return;
      }

      if (importKind === 'types') {
        const parsed = parseTypesWorkbook(buf);
        if ('error' in parsed) { setParseError(parsed.error); return; }
        setTypeRows(parsed.rows);
        setUnitPreview(null);
        setStep('preview');
        return;
      }

      const parsed = parseUnitsWorkbook(buf);
      if ('error' in parsed) { setParseError(parsed.error); return; }

      await Promise.all([
        useInventoryStore.getState().fetchItems(),
        useOwnersStore.getState().fetchOwners(),
      ]);

      const inv = useInventoryStore.getState();
      const wh = useWarehouseStore.getState();
      const owners = useOwnersStore.getState();
      const preview = buildUnitPreview(parsed.rows, {
        items: inv.items,
        stockUnits: inv.stockUnits,
        warehouses: wh.warehouses,
        storageAreasByWarehouse: wh.storageAreasByWarehouse,
        defaultStorageAreaIdByWarehouse: wh.defaultStorageAreaIdByWarehouse,
        owners: owners.owners,
        fallbackWarehouseName: fallbackWarehouseName(),
      });
      setUnitPreview(preview);
      setTypeRows([]);
      setStep('preview');
    };
    reader.readAsArrayBuffer(file);
  };

  const onFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const runImport = async () => {
    setIsImporting(true);
    try {
      if (importKind === 'types') {
        const res = await runTypesImport(typeRows, importMode, duplicateAction);
        setTypesResult(res);
        setUnitsResult(null);
      } else {
        const preview = unitPreview;
        const res = await runUnitsImport(preview?.validRows ?? [], fallbackWarehouseName());
        if (preview && preview.blocked.length > 0) {
          const blockedErrors = preview.blocked.map(issue =>
            issue.rowNumber ? `Row ${issue.rowNumber} (${issue.name}): ${issue.message}` : issue.message,
          );
          res.skipped += preview.blocked.length;
          res.errors = [...blockedErrors, ...res.errors];
        }
        setUnitsResult(res);
        setTypesResult(null);
      }
      setStep('result');
      onDone();
    } finally {
      setIsImporting(false);
    }
  };

  const previewCount = importKind === 'types'
    ? typeRows.length
    : (unitPreview?.validRows.length ?? 0);

  const selectedCard = (active: boolean) => ({
    backgroundColor: active ? (isDark ? '#1d2535' : '#eff6ff') : inputBg,
    borderColor: active ? '#3b82f6' : border,
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} onClick={onClose} />

      <div
        className="relative flex flex-col rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh]"
        style={{ backgroundColor: modalBg, border: `1px solid ${border}` }}
      >
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={18} className="text-green-400 flex-shrink-0" />
            <div>
              <p className="text-[11px] uppercase tracking-wide" style={{ color: muted }}>Excel</p>
              <h2 className="text-base font-semibold mt-1" style={{ color: text }}>Import from Excel</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-700/50 transition-colors" style={{ color: muted }}>
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {step === 'options' && (
            <div className="space-y-5">
              <div>
                <label className={labelCls} style={{ color: text }}>What to import</label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  {([
                    { value: 'types' as const, title: 'Item Types only', desc: 'Create or update the item catalogue. No stock units are added.' },
                    { value: 'units' as const, title: 'Item Types and Inventory', desc: 'One row per stock unit. Missing types, warehouses, areas, and owners are created.' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setImportKind(opt.value);
                        setParseError('');
                        if (opt.value === 'units') setImportMode('add');
                      }}
                      className="text-left rounded-lg p-3.5 border transition-colors"
                      style={selectedCard(importKind === opt.value)}
                    >
                      <p className="text-sm font-medium" style={{ color: importKind === opt.value ? '#60a5fa' : text }}>{opt.title}</p>
                      <p className="text-sm mt-1 leading-relaxed" style={{ color: muted }}>{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {importKind === 'types' && (
                <div>
                  <label className={labelCls} style={{ color: text }}>Import mode</label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    {([
                      { value: 'add' as const, title: 'Add', desc: 'Append items from the file to your existing catalogue.' },
                      { value: 'replace' as const, title: 'Replace', desc: 'Delete all existing items, then insert from the file.' },
                    ]).map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setImportMode(opt.value)}
                        className="text-left rounded-lg p-3.5 border transition-colors"
                        style={selectedCard(importMode === opt.value)}
                      >
                        <p className="text-sm font-medium" style={{ color: importMode === opt.value ? '#60a5fa' : text }}>{opt.title}</p>
                        <p className="text-sm mt-1 leading-relaxed" style={{ color: muted }}>{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                  {importMode === 'replace' && (
                    <div
                      className="flex items-start gap-2 mt-3 rounded-lg p-3 text-sm"
                      style={{ backgroundColor: isDark ? '#450a0a30' : '#fef2f2', border: `1px solid ${isDark ? '#7f1d1d50' : '#fecaca'}`, color: isDark ? '#f87171' : '#dc2626' }}
                    >
                      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                      <span>All existing items and their associated stock units will be permanently deleted before importing.</span>
                    </div>
                  )}
                </div>
              )}

              {importKind === 'types' && importMode === 'add' && (
                <div>
                  <label className={labelCls} style={{ color: text }}>If an item already exists (matched by name + SKU)</label>
                  <div className="space-y-2.5 mt-1">
                    {([
                      { value: 'skip' as const, label: 'Skip duplicate', desc: 'Leave the existing item unchanged.' },
                      { value: 'update' as const, label: 'Update with Excel values', desc: 'Overwrite category and notes from the file.' },
                      { value: 'add_new' as const, label: 'Add as new entry anyway', desc: 'Allow multiple items with the same name/SKU.' },
                    ]).map(opt => (
                      <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer">
                        <input
                          type="radio"
                          name="duplicate"
                          checked={duplicateAction === opt.value}
                          onChange={() => setDuplicateAction(opt.value)}
                          className="mt-1 flex-shrink-0 w-4 h-4"
                        />
                        <span>
                          <span className="text-sm font-medium" style={{ color: text }}>{opt.label}</span>
                          <span className="text-sm ml-2" style={{ color: muted }}>{opt.desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {importKind === 'units' && (
                <div className="rounded-lg p-3.5 border space-y-1.5" style={{ backgroundColor: pageBg, borderColor: border }}>
                  <p className="text-sm font-medium" style={{ color: text }}>This import always adds</p>
                  <p className="text-sm leading-relaxed" style={{ color: muted }}>
                    Nothing is deleted. Existing item types, warehouses, storage areas, and owners are reused by name; missing ones are created. Each Excel row becomes a new stock unit. Blank warehouse uses{' '}
                    <span style={{ color: text }}>{fallbackWarehouseName() ?? 'the selected warehouse'}</span>.
                  </p>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={labelCls} style={{ color: text }}>Excel file</label>
                  <button
                    type="button"
                    onClick={importKind === 'types' ? downloadInventoryTemplate : downloadInventoryUnitsTemplate}
                    className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-blue-400"
                    style={{ color: muted }}
                  >
                    <Download size={14} />
                    Download Template
                  </button>
                </div>
                <div
                  onDrop={onDrop}
                  onDragOver={e => e.preventDefault()}
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-lg border-2 border-dashed flex flex-col items-center justify-center py-8 gap-2 cursor-pointer transition-colors hover:border-blue-500/60"
                  style={{ borderColor: isDark ? '#3a404a' : '#d1d5db', backgroundColor: pageBg }}
                >
                  <Upload size={22} style={{ color: muted }} />
                  <p className="text-sm font-medium" style={{ color: text }}>Drop file here or click to browse</p>
                  <p className="text-sm" style={{ color: muted }}>.xlsx or .xls</p>
                </div>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onFilePick} />
                {parseError && (
                  <p className="text-sm text-red-400 mt-2 flex items-center gap-1.5">
                    <AlertTriangle size={14} /> {parseError}
                  </p>
                )}
              </div>
            </div>
          )}

          {step === 'preview' && importKind === 'types' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: muted }}>
                  {typeRows.length} row{typeRows.length !== 1 ? 's' : ''} from {fileName}
                </p>
                <button
                  type="button"
                  onClick={() => { resetFile(); setStep('options'); }}
                  className="text-sm hover:text-blue-400 transition-colors"
                  style={{ color: muted }}
                >
                  ← Change file
                </button>
              </div>
              <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${border}` }}>
                <table className="w-full">
                  <thead style={{ backgroundColor: headerBg }}>
                    <tr style={{ borderBottom: `1px solid ${border}` }}>
                      {['Item Name', 'SKU', 'Category', 'Notes'].map(colName => (
                        <th key={colName} className="px-3 py-2 text-left text-[11px] uppercase tracking-wide font-medium" style={{ color: muted }}>
                          {colName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: modalBg }}>
                    {typeRows.map((row, i) => (
                      <tr key={i} style={{ borderBottom: i < typeRows.length - 1 ? `1px solid ${border}` : undefined }}>
                        <td className="px-3 py-2 text-sm font-medium" style={{ color: text }}>{row.name}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: row.sku ? text : muted }}>{row.sku || '—'}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: row.category ? text : muted }}>{row.category || '—'}</td>
                        <td className="px-3 py-2 text-sm" style={{ color: row.notes ? text : muted }}>{row.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="rounded-lg p-3 text-sm" style={{ backgroundColor: pageBg, border: `1px solid ${border}`, color: muted }}>
                Mode:{' '}
                <span style={{ color: text }}>
                  {importMode === 'replace'
                    ? 'Replace (all existing items will be deleted first)'
                    : `Add — ${duplicateAction === 'skip' ? 'skip duplicates' : duplicateAction === 'update' ? 'update duplicates' : 'allow duplicates'}`}
                </span>
              </div>
            </div>
          )}

          {step === 'preview' && importKind === 'units' && unitPreview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm" style={{ color: muted }}>
                  {unitPreview.validRows.length} unit{unitPreview.validRows.length !== 1 ? 's' : ''} ready from {fileName}
                </p>
                <button
                  type="button"
                  onClick={() => { resetFile(); setStep('options'); }}
                  className="text-sm hover:text-blue-400 transition-colors"
                  style={{ color: muted }}
                >
                  ← Change file
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: 'Units', value: unitPreview.summary.unitsToCreate },
                  { label: 'New types', value: unitPreview.summary.newItemTypes.length },
                  { label: 'New locations', value: unitPreview.summary.newWarehouses.length + unitPreview.summary.newStorageAreas.length },
                  { label: 'New owners', value: unitPreview.summary.newOwners.length },
                ].map(stat => (
                  <div key={stat.label} className="rounded-lg p-3 text-center" style={{ backgroundColor: pageBg, border: `1px solid ${border}` }}>
                    <p className="text-xl font-semibold" style={{ color: text }}>{stat.value}</p>
                    <p className="text-[11px] uppercase tracking-wide mt-1" style={{ color: muted }}>{stat.label}</p>
                  </div>
                ))}
              </div>

              {(unitPreview.summary.newItemTypes.length > 0 || unitPreview.summary.discontinuedItemTypes.length > 0) && (
                <div className="text-sm space-y-1" style={{ color: muted }}>
                  {unitPreview.summary.newItemTypes.length > 0 && (
                    <p>
                      <span style={{ color: text }}>New item types: </span>
                      {unitPreview.summary.newItemTypes.join(', ')}
                    </p>
                  )}
                  {unitPreview.summary.discontinuedItemTypes.length > 0 && (
                    <p className="text-amber-400">
                      Discontinued types will still receive stock: {unitPreview.summary.discontinuedItemTypes.join(', ')}
                    </p>
                  )}
                  {unitPreview.summary.newWarehouses.length > 0 && (
                    <p>
                      <span style={{ color: text }}>New warehouses: </span>
                      {unitPreview.summary.newWarehouses.join(', ')}
                    </p>
                  )}
                  {unitPreview.summary.newStorageAreas.length > 0 && (
                    <p>
                      <span style={{ color: text }}>New storage areas: </span>
                      {unitPreview.summary.newStorageAreas.map(a => `${a.warehouse} / ${a.area}`).join(', ')}
                    </p>
                  )}
                  {unitPreview.summary.newOwners.length > 0 && (
                    <p>
                      <span style={{ color: text }}>New owners: </span>
                      {unitPreview.summary.newOwners.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {unitPreview.blocked.length > 0 && (
                <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: isDark ? '#450a0a20' : '#fef2f2', border: `1px solid ${isDark ? '#7f1d1d40' : '#fecaca'}` }}>
                  <p className="text-sm font-medium text-red-400">
                    {unitPreview.blocked.length} row{unitPreview.blocked.length !== 1 ? 's' : ''} will be skipped
                  </p>
                  {unitPreview.blocked.slice(0, 12).map((issue, i) => (
                    <p key={i} className="text-sm text-red-400/80">
                      {issue.rowNumber ? `Row ${issue.rowNumber}` : 'File'}{issue.name ? ` (${issue.name})` : ''}: {issue.message}
                    </p>
                  ))}
                  {unitPreview.blocked.length > 12 && (
                    <p className="text-sm text-red-400/80">…and {unitPreview.blocked.length - 12} more</p>
                  )}
                </div>
              )}

              {unitPreview.warnings.length > 0 && (
                <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: isDark ? '#451a0320' : '#fff7ed', border: `1px solid ${isDark ? '#7c2d1230' : '#fed7aa'}` }}>
                  <p className="text-sm font-medium" style={{ color: isDark ? '#fdba74' : '#c2410c' }}>
                    {unitPreview.warnings.length} duplicate serial warning{unitPreview.warnings.length !== 1 ? 's' : ''} — rows will still import
                  </p>
                  {unitPreview.warnings.slice(0, 8).map((issue, i) => (
                    <p key={i} className="text-sm" style={{ color: isDark ? '#fdba74' : '#c2410c' }}>
                      Row {issue.rowNumber} ({issue.name}): {issue.message}
                    </p>
                  ))}
                </div>
              )}

              {unitPreview.sampleRows.length > 0 && (
                <div className="rounded-lg overflow-x-auto" style={{ border: `1px solid ${border}` }}>
                  <table className="w-full min-w-[720px]">
                    <thead style={{ backgroundColor: headerBg }}>
                      <tr style={{ borderBottom: `1px solid ${border}` }}>
                        {['Item Name', 'Serial/MAC', 'Warehouse', 'Storage Area', 'Status', 'Owner'].map(colName => (
                          <th key={colName} className="px-3 py-2 text-left text-[11px] uppercase tracking-wide font-medium" style={{ color: muted }}>
                            {colName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {unitPreview.sampleRows.map((row, i) => (
                        <tr key={i} style={{ borderBottom: i < unitPreview.sampleRows.length - 1 ? `1px solid ${border}` : undefined }}>
                          <td className="px-3 py-2 text-sm font-medium" style={{ color: text }}>{row.name}</td>
                          <td className="px-3 py-2 text-sm" style={{ color: row.serialMac ? text : muted }}>{row.serialMac || '—'}</td>
                          <td className="px-3 py-2 text-sm" style={{ color: text }}>{row.warehouse || fallbackWarehouseName() || '—'}</td>
                          <td className="px-3 py-2 text-sm" style={{ color: row.storageArea ? text : muted }}>{row.storageArea || 'default'}</td>
                          <td className="px-3 py-2 text-sm" style={{ color: text }}>{row.statusRaw || 'in stock'}</td>
                          <td className="px-3 py-2 text-sm" style={{ color: row.owner ? text : muted }}>{row.owner || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {unitPreview.validRows.length > unitPreview.sampleRows.length && (
                    <p className="px-3 py-2 text-sm" style={{ color: muted, borderTop: `1px solid ${border}` }}>
                      Showing first {unitPreview.sampleRows.length} of {unitPreview.validRows.length} units
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 'result' && typesResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                <p className="text-base font-semibold" style={{ color: text }}>Import complete</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Added', value: typesResult.added, color: '#4ade80' },
                  { label: 'Updated', value: typesResult.updated, color: '#60a5fa' },
                  { label: 'Skipped', value: typesResult.skipped, color: muted },
                ].map(stat => (
                  <div key={stat.label} className="rounded-lg p-3 text-center" style={{ backgroundColor: pageBg, border: `1px solid ${border}` }}>
                    <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                    <p className="text-[11px] uppercase tracking-wide mt-0.5" style={{ color: muted }}>{stat.label}</p>
                  </div>
                ))}
              </div>
              {typesResult.errors.length > 0 && (
                <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: isDark ? '#450a0a20' : '#fef2f2', border: `1px solid ${isDark ? '#7f1d1d40' : '#fecaca'}` }}>
                  <p className="text-sm font-medium text-red-400">
                    {typesResult.errors.length} error{typesResult.errors.length !== 1 ? 's' : ''}
                  </p>
                  {typesResult.errors.map((err, i) => (
                    <p key={i} className="text-sm text-red-400/80">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'result' && unitsResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
                <p className="text-base font-semibold" style={{ color: text }}>Import complete</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Units added', value: unitsResult.unitsCreated, color: '#4ade80' },
                  { label: 'Item types created', value: unitsResult.itemsCreated, color: '#60a5fa' },
                  { label: 'Skipped', value: unitsResult.skipped, color: muted },
                  { label: 'Warehouses created', value: unitsResult.warehousesCreated, color: '#60a5fa' },
                  { label: 'Areas created', value: unitsResult.areasCreated, color: '#60a5fa' },
                  { label: 'Owners created', value: unitsResult.ownersCreated, color: '#60a5fa' },
                ].map(stat => (
                  <div key={stat.label} className="rounded-lg p-3 text-center" style={{ backgroundColor: pageBg, border: `1px solid ${border}` }}>
                    <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
                    <p className="text-[11px] uppercase tracking-wide mt-0.5" style={{ color: muted }}>{stat.label}</p>
                  </div>
                ))}
              </div>
              {unitsResult.errors.length > 0 && (
                <div className="rounded-lg p-3 space-y-1 max-h-48 overflow-y-auto" style={{ backgroundColor: isDark ? '#450a0a20' : '#fef2f2', border: `1px solid ${isDark ? '#7f1d1d40' : '#fecaca'}` }}>
                  <p className="text-sm font-medium text-red-400">
                    {unitsResult.errors.length} error{unitsResult.errors.length !== 1 ? 's' : ''}
                  </p>
                  {unitsResult.errors.map((err, i) => (
                    <p key={i} className="text-sm text-red-400/80">{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${border}` }}>
          {step === 'result' ? (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm rounded border transition-colors hover:bg-gray-700/30"
                style={{ borderColor: border, color: muted }}
              >
                Cancel
              </button>
              {step === 'options' && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                >
                  Choose File
                </button>
              )}
              {step === 'preview' && (
                <button
                  type="button"
                  onClick={() => void runImport()}
                  disabled={isImporting || previewCount === 0}
                  className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50"
                >
                  {isImporting
                    ? 'Importing…'
                    : importKind === 'types'
                      ? `Import ${previewCount} item${previewCount !== 1 ? 's' : ''}`
                      : `Import ${previewCount} unit${previewCount !== 1 ? 's' : ''}`}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelImportModal;
